import React, { useState } from "react";
import "../styles/PricingTable.css";
import { useSubscriptionActions } from "../hooks/index.js";

const PricingTable = ({
  currentSubscription = null,
  onSubscribe = () => {},
  email = null,
}) => {
  const [isYearly, setIsYearly] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const { manageSubscription } = useSubscriptionActions();

  const getStripeLink = (link) => {
    if (!email) return link;
    const separator = link.includes("?") ? "&" : "?";
    return `${link}${separator}prefilled_email=${encodeURIComponent(email)}`;
  };

  const hasActiveSubscription =
    currentSubscription && currentSubscription !== "personal";

  const handleManageSubscription = async () => {
    setLoadingPortal(true);
    try {
      await manageSubscription();
    } catch (error) {
      console.error("Error accessing customer portal:", error);
    } finally {
      setLoadingPortal(false);
    }
  };

  const tiers = [
    {
      name: "Personal",
      monthlyPrice: 0,
      yearlyPrice: 0,
      description: "Subscription",
      features: [
        "Free Forever",
        "Unlimited Tasks",
        "1 Category",
        "1 Slate",
        "Quick Add Tasks",
        "Visual Prioritization",
      ],
      id: "personal",
      monthlyLink: "https://buy.stripe.com/dRm00j36wbGS34y1bF9k401",
      yearlyLink: "https://buy.stripe.com/00w4gz0Yo3am48Cg6z9k402",
    },
    {
      name: "Plus",
      monthlyPrice: 3,
      yearlyPrice: 29,
      description: "Subscription",
      features: [
        "Everything in Personal +",
        "10 Color-coded Categories",
        "3 Slates to Sort Work, Personal, Other",
        "Task Timing",
        "Recurring Tasks",
        "Projects & Sub-Tasks",
      ],
      id: "plus",
      monthlyLink: "https://buy.stripe.com/eVqaEX7mMh1c9sW1bF9k403",
      yearlyLink: "https://buy.stripe.com/3cI3cv7mM3ambB42fJ9k404",
    },
    {
      name: "Pro",
      monthlyPrice: 5,
      yearlyPrice: 49,
      description: "Subscription",
      features: [
        "Everything in Plus +",
        "Immediate Access to the Following Features",
        "Shared Tasks (coming soon)",
        "Filters & Reports (coming soon)",
        "AI Task Analysis (coming soon)",
        "Strategic Planning Session Facilitator (coming soon)",
      ],
      id: "pro",
      monthlyLink: "https://buy.stripe.com/5kQ00jayY8uG9sWcUn9k405",
      yearlyLink: "https://buy.stripe.com/4gMfZh9uU6myfRkdYr9k406",
    },
  ];

  const getCurrentPrice = (monthlyPrice, yearlyPrice) => {
    return isYearly ? yearlyPrice : monthlyPrice;
  };

  const getTierIndex = (tierId) => {
    return tiers.findIndex((t) => t.id === tierId);
  };

  const isLowerTier = (tierId) => {
    if (!currentSubscription) return false;
    return getTierIndex(tierId) < getTierIndex(currentSubscription);
  };

  const getButtonText = (tierId) => {
    if (currentSubscription === tierId) {
      return "Current";
    }
    if (isLowerTier(tierId)) {
      return "Included";
    }
    return "Subscribe";
  };

  const handleSubscribe = (tier) => {
    if (currentSubscription !== tier.id) {
      const paymentLink = isYearly ? tier.yearlyLink : tier.monthlyLink;
      const linkWithEmail = getStripeLink(paymentLink);
      window.location.href = linkWithEmail;
    }
  };

  return (
    <div className="pricing-table-container">
      <div className="pricing-header">
        <div className="pricing-header-top">
          <h1>Choose Your Plan</h1>
          {hasActiveSubscription && (
            <button
              className="manage-subscription-link"
              onClick={handleManageSubscription}
              disabled={loadingPortal}
            >
              {loadingPortal ? "Loading..." : "Manage Subscription"}
            </button>
          )}
        </div>
        <div className="toggle-container">
          <span className={!isYearly ? "active" : ""}>Monthly</span>
          <button
            className={`toggle-switch ${isYearly ? "yearly" : "monthly"}`}
            onClick={() => setIsYearly(!isYearly)}
            aria-label="Toggle between monthly and yearly billing"
          >
            <div className="toggle-slider"></div>
          </button>
          <span className={isYearly ? "active" : ""}>Yearly</span>
          {isYearly && <span className="savings-badge">Save 20%</span>}
        </div>
      </div>

      <div className="pricing-cards-container">
        {tiers.map((tier) => (
          <div
            key={tier.id}
            className={`pricing-card ${
              currentSubscription === tier.id ? "current" : ""
            }`}
          >
            <div className="pricing-card-header">
              <h2>PriorityPop {tier.name}</h2>
              <p className="tier-description">{tier.description}</p>
            </div>

            <div className="pricing-section">
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">
                  {getCurrentPrice(tier.monthlyPrice, tier.yearlyPrice)}
                </span>
              </div>
              <p className="billing-period">
                per {isYearly ? "year" : "month"}
              </p>
            </div>

            <button
              className={`subscribe-button ${
                currentSubscription === tier.id ? "current-button" : ""
              } ${isLowerTier(tier.id) ? "disabled-tier" : ""}`}
              onClick={() => handleSubscribe(tier)}
              disabled={currentSubscription === tier.id || isLowerTier(tier.id)}
            >
              {getButtonText(tier.id)}
            </button>

            <div className="features-section">
              <p className="features-header">This includes:</p>
              <ul className="features-list">
                {tier.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PricingTable;
